<template>
  <v-card color="background lighten-3" elevation="0" rounded="lg">
    <v-card-text :class="{ 'pa-0': noPadding }">
      <v-row>
        <v-col cols="12" md="8" class="d-flex justify-center flex-column pa-0 mb-5">
          <v-row v-if="mobile">
            <template v-for="step in steps" :key="step.id">
              <v-col cols="12" class="d-flex justify-center align-center">
                <v-avatar size="48" color="primary">
                  <span class="text-white text-bold"> {{ step.id }} </span>
                </v-avatar>
              </v-col>
              <v-col class="text-center pa-0 px-4" cols="12">
                <span> {{ step.text.mobile }} </span>
              </v-col>
            </template>
          </v-row>
          <v-row v-if="!mobile" class="steps">
            <template v-for="step in steps" :key="step.id">
              <v-row class="step">
                <v-avatar size="36" color="primary" style="color: white">
                  {{ step.id }}
                </v-avatar>
                &nbsp;&nbsp;&nbsp;&nbsp;
                <span class="step-text">{{ step.text.desktop }}</span>
              </v-row>
            </template>
          </v-row>
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-stretch justify-center flex-column">
          <v-row v-if="payment !== null && payment.code !== undefined" justify="center">
            <v-col md="6" cols="12" class="d-flex justify-center">
              <v-btn
                block
                color="success"
                disabled
                size="x-large"
              >
                <t-countdown :timer="timer" @stop="emit('expired')" />
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="justify-center align-center">
            <v-col cols="12" class="d-flex justify-center align-center">
              <v-card
                elevation="0"
                color="#e3e3e3"
                style="width: 100%; height: 225px"
                class="d-flex justify-center align-center"
              >
                <qr-code
                  :value="code"
                  :style="filter"
                  background="transparent"
                  render-as="svg"
                  level="M"
                  :size="175"
                />
              </v-card>
            </v-col>
          </v-row>
          <v-row class="justify-center">
            <v-col
              v-if="payment !== null && payment.code !== undefined"
              md="6"
              cols="12"
              class="d-flex justify-center"
            >
              <v-btn
                block
                color="info"
                size="x-large"
                @click="copy"
              >
                Copiar Código
              </v-btn>
            </v-col>
            <v-col
              v-if="!(payment !== null && payment.code !== undefined)"
              md="6"
              cols="12"
              class="d-flex justify-end"
            >
              <v-btn
                block
                color="success"
                :disabled="(payment !== null && payment.code !== undefined) || loading"
                :loading="loading"
                size="x-large"
                @click="submit"
              >
                <span v-if="payment === null"> Gerar Código </span>
                <t-countdown v-else :timer="timer" @stop="emit('expired')" />
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
    <div class="pa-4">
      <t-alert color="grey-lighten-2">
        <b>COMPRA SEGURA!</b> A INGRESSO DE VANTAGENS NÃO FAZ SOLICITAÇÔES DE PAGAMENTOS VIA <b>PIX</b>, NEM POR E-MAIL,
        NEM POR NENHUM OUTRO CANAL DE ATENDIMENTO. DENUNCIE QUALQUER SOLICITAÇÃO SUSPEITA ENTRANDO IMEDIATAMENTE EM
        CONTATO COM A NOSSA <a href="https://ingressodevantagens.com.br/suporte">CENTRAL DE ATENDIMENTO</a>.
      </t-alert>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from "vue";
import { useDisplay } from "vuetify";
import { DateTime } from "luxon";
import QrCode from "qrcode.vue";
import { useSnackbar } from "@/stores/snackbar";
import PixPayment from "@/types/PixPayment";
import TCountdown from "@/components/core/TCountdown.vue";
import TAlert from "@/components/core/TAlert.vue";
import PaymentData from "@/types/PaymentData";
import PaymentMethod from "@/types/PaymentMethod";
import { useLoader } from "@/stores/loader";
import { useCheckoutStore } from "@/stores/checkout";

interface Props {
  pix?: PixPayment | null;
  noPadding?: boolean;
  secondsToTimer?: number;
  method: PaymentMethod;
}

const loader = useLoader();

const props = withDefaults(defineProps<Props>(), {
  pix: null,
  noPadding: false,
  secondsToTimer: 0,
});

const emit = defineEmits<{
  expired: [];
  submit: [];
}>();

const snackbar = useSnackbar();
const checkout = useCheckoutStore();
const { mobile } = useDisplay();

const timer = ref<number>(900);

const steps = [
  {
    id: 1,
    text: {
      mobile: "Após gerar o código PIX, clique no botão COPIAR CÓDIGO",
      desktop: "Após gerar o código PIX, clique no botão COPIAR CÓDIGO ou LEIA O QR CODE pelo seu smartphone",
    },
  },
  {
    id: 2,
    text: {
      mobile: "Abra o aplicativo do seu banco, vá até a seção PIX e escolha a opção COPIA E COLA",
      desktop: "Abra o aplicativo do seu banco, vá até a seção PIX e informe o código PIX gerado",
    },
  },
  {
    id: 3,
    text: {
      mobile: "Após COLAR verifique os dados do recebedor e o valor da operação para confirmar o pagamento",
      desktop: "Em seguida, verifique os dados do recebedor e o valor da operação para confirmar o pagamento",
    },
  },
  {
    id: 4,
    text: {
      mobile:
        "Em seguida, retorne aqui e aguarde por alguns segundos até a confirmação do seu pagamento e geração automática do seu voucher",
      desktop:
        "Pronto! Retorne aqui e aguarde por alguns segundos até a confirmação do seu pagamento e geração automática do seu voucher",
    },
  },
];

const loading = computed((): boolean => loader.loading("button"));

const payment = computed((): PixPayment | null => {
  if (props.pix !== undefined && props.pix !== null) {
    return props.pix;
  }

  return checkout.transaction as PixPayment | null;
});

const filter = computed((): string => {
  if (payment.value?.code === undefined) {
    return "filter: blur(8px)";
  }

  return "";
});

const code = computed((): string => {
  if (payment.value === null) return "PIX";

  return payment.value.code ?? "PIX";
});

onBeforeMount(() => {
  if (payment.value !== null) {
    const expiration = DateTime.fromFormat(payment.value.expiration, "y-MM-dd HH:mm:ss");
    const diff = expiration.diff(DateTime.now(), [ "seconds" ]).toObject();

    if (diff.seconds !== undefined) {
      timer.value = diff.seconds;
    }
  }

  if (props.secondsToTimer) {
    timer.value = props.secondsToTimer;
  }
});

function submit() {
  emit("submit");
}

function validate() {
  return true;
}

async function pay() {
  const data = {} as PaymentData;

  return checkout.pay(props.method, data);
}

function copy() {
  window.navigator.clipboard.writeText(code.value);
  snackbar.show("Código copiado!", "success");
}

defineExpose({
  validate,
  pay,
});
</script>

<style>
.steps {
  margin: 2rem 8rem 0 2rem;
}
.steps .step {
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
  align-content: center;
  max-height: 60px;
  border-bottom: 1px solid black;
  padding-bottom: 1rem;
  padding-top: 1rem;
}
.steps .step:last-child {
  border-bottom: unset;
}
.steps .step .step-text {
  display: flex;
  align-items: center;
  word-break: normal;
  width: 80%;
}
</style>
